import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { writeToClipboard } from "../lib/writeToClipboard";
import { Button } from "./Button";
import { ClickButton } from "./ClickButton";
import { CopyOpenIcon } from "./CopyOpenIcon";
import { Text } from "./Text";
import type { StepData } from "./Workflow";

const Header = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  height: 100%;
`;

const Pill = styled(ClickButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 38px;
  background: var(--alpha4);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  color: var(--text-primary);

  &:hover {
    background: var(--alpha8);
  }
`;

type Props = {
  step: StepData;
  email?: string | null;
  right?: React.ReactNode;
  onBack?: () => void;
};

export function StepHeader({ step, email, right, onBack }: Props) {
  return (
    <Header>
      {onBack && (
        <Button
          variant="secondary"
          size="l"
          icon="arrow-left"
          type="button"
          aria-label="Back"
          onMouseDown={onBack}
        />
      )}
      <div
        style={{
          flex: 1,
          width: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <div style={{ minWidth: 0, padding: "8px 0" }}>
          <Text variant="text-regular-md" ellipsis>
            {step.name}
          </Text>
        </div>
        {email && (
          <CopyPill
            displayContent={email}
            copyText={email}
            openUrl={`mailto:${email}`}
            title={email}
          />
        )}
      </div>
      {right}
    </Header>
  );
}

type CopyPillProps = {
  displayContent: React.ReactNode;
  copyText: string;
  openUrl: string;
  title: string;
};

function CopyPill({ displayContent, copyText, openUrl, title }: CopyPillProps) {
  const [copied, setCopied] = useState(false);
  const [cmdHeld, setCmdHeld] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => setCmdHeld(e.metaKey);
    const onBlur = () => setCmdHeld(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (e.metaKey) {
      window.open(openUrl, "_blank");
      return;
    }
    void writeToClipboard(copyText);
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const iconState = copied ? "copied" : cmdHeld ? "cmd" : undefined;

  return (
    <Pill type="button" onClick={handleClick} title={title}>
      <Text variant="text-small-md" color="var(--text-primary)" ellipsis>
        {displayContent}
      </Text>
      <CopyOpenIcon state={iconState} size={10} />
    </Pill>
  );
}
